import { useRef, useEffect } from 'react';
import { Download } from 'lucide-react';
import { Spinner } from '../../components/ui';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FeedbackFilters {
  days: 7 | 14 | 30 | 90;
  feedbackType: string;
  role: string;
  entityId: string;
}

interface FeedbackFiltersProps {
  value: FeedbackFilters;
  onChange: (f: FeedbackFilters) => void;
  onExport: () => void;
  isExporting: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FeedbackFiltersBar({
  value,
  onChange,
  onExport,
  isExporting,
}: FeedbackFiltersProps) {
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce entityId changes
  const handleEntityId = (raw: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onChange({ ...value, entityId: raw });
    }, 400);
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const selectCls =
    'bg-gray-900 border border-white/[0.08] rounded-lg px-2.5 py-1.5 text-xs text-white ' +
    'focus:outline-none focus:border-primary/50 transition-colors';

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      {/* Days */}
      <select
        value={value.days}
        onChange={e => onChange({ ...value, days: Number(e.target.value) as FeedbackFilters['days'] })}
        className={selectCls}
      >
        <option value={7}>Last 7 days</option>
        <option value={14}>Last 14 days</option>
        <option value={30}>Last 30 days</option>
        <option value={90}>Last 90 days</option>
      </select>

      {/* Type */}
      <select
        value={value.feedbackType}
        onChange={e => onChange({ ...value, feedbackType: e.target.value })}
        className={selectCls}
      >
        <option value="">All Types</option>
        <option value="feature_rating">Feature Rating</option>
        <option value="usefulness">Usefulness</option>
        <option value="nps">NPS</option>
        <option value="freeform">Freeform</option>
      </select>

      {/* Role */}
      <select
        value={value.role}
        onChange={e => onChange({ ...value, role: e.target.value })}
        className={selectCls}
      >
        <option value="">All Roles</option>
        <option value="admin">admin</option>
        <option value="executive">executive</option>
        <option value="product_manager">product_manager</option>
        <option value="team_lead">team_lead</option>
        <option value="engineer">engineer</option>
        <option value="designer">designer</option>
        <option value="sales">sales</option>
        <option value="marketing">marketing</option>
        <option value="viewer">viewer</option>
      </select>

      {/* Entity ID */}
      <input
        type="text"
        defaultValue={value.entityId}
        placeholder="Entity ID..."
        onChange={e => handleEntityId(e.target.value)}
        className={`${selectCls} w-36 placeholder:text-gray-400`}
      />

      {/* Export — right-aligned */}
      <div className="ml-auto">
        <button
          type="button"
          onClick={onExport}
          disabled={isExporting}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors
            bg-white/[0.06] text-gray-300 hover:bg-white/10 border border-white/[0.08]
            disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {isExporting ? (
            <Spinner className="h-3 w-3" />
          ) : (
            <Download className="h-3 w-3" />
          )}
          Export CSV
        </button>
      </div>
    </div>
  );
}
